import { useMemo } from "react";
import { useSelector } from "react-redux";
import { KnownOrganizationIds, ViewId } from "@madhive/mad-sdk";
import {
  selectCurrentUser,
  selectProductionViews
} from "appReducers/authReducer";
import { useFeatureFlags } from "hooks/useFeatureFlag/useFeatureFlag";
import useManageDealsView from "hooks/useManageDealsView";
import useManagePricesView from "hooks/useManagePricesView";
import { madSDK } from "lib/sdk";
import {
  MAIN_MENU_FLAGS,
  MainMenuContext,
  resolveInternalTools,
  resolveMainMenuViews
} from "./main-menu-views";

const MAIN_MENU_FLAG_KEYS = Object.values(MAIN_MENU_FLAGS);

const useMainMenuFlags = (): MainMenuContext["flags"] => {
  const enabled = useFeatureFlags(MAIN_MENU_FLAG_KEYS);

  return useMemo(
    () =>
      Object.fromEntries(
        Object.entries(MAIN_MENU_FLAGS).map(([name, key]) => [
          name,
          enabled[key]
        ])
      ) as MainMenuContext["flags"],
    [enabled]
  );
};

const useMainMenuContext = (assignedViews: ViewId[]): MainMenuContext => {
  const user = useSelector(selectCurrentUser);
  const orgProductionViews = useSelector(selectProductionViews);
  const canViewManageDeals = useManageDealsView();
  const canViewManagePrices = useManagePricesView();
  const flags = useMainMenuFlags();
  const isRootUser = madSDK.isRootUser();

  const isDev = !!user?.isDev;
  const isMadhiveAdmin =
    user?.primaryOrganizationId === KnownOrganizationIds.MADHIVE_DEFAULT &&
    !!user?.isAdmin;

  return useMemo(
    () => ({
      assignedViews: new Set(assignedViews),
      orgProductionViews,
      isDev,
      isRootUser,
      isMadhiveAdmin,
      canViewManageDeals,
      canViewManagePrices,
      flags
    }),
    [
      assignedViews,
      orgProductionViews,
      isDev,
      isRootUser,
      isMadhiveAdmin,
      canViewManageDeals,
      canViewManagePrices,
      flags
    ]
  );
};

/** Main menu views and internal tools for the current user. */
export const useMainMenuViews = (assignedViews: ViewId[]) => {
  const ctx = useMainMenuContext(assignedViews);

  return useMemo(
    () => ({
      views: resolveMainMenuViews(ctx),
      internalTools: resolveInternalTools(ctx)
    }),
    [ctx]
  );
};
